<script setup>
// build-force: 1776165600
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

const store = useStore();
const route = useRoute();
const router = useRouter();

const isOpen = ref(false);
const iframeRef = ref(null);
const iframeReady = ref(false);
const isMobile = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);

const conversationId = computed(() => {
  return route.params.conversation_id || route.params.conversationId || '';
});

const cwToken = computed(() => {
  const user = currentUser.value;
  return user?.access_token || '';
});

// URL estática — carrega uma vez, contexto atualiza via postMessage
const iframeUrl = computed(() => {
  const base = '/ai-chat/embed';
  const isDark = document.body.classList.contains('dark');
  const parts = [];
  if (cwToken.value) parts.push('cw_token=' + encodeURIComponent(cwToken.value));
  parts.push('theme=' + (isDark ? 'dark' : 'light'));
  return base + '?' + parts.join('&');
});

function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

function toggle() {
  isOpen.value = !isOpen.value;
  window.dispatchEvent(new CustomEvent("sz-sidebar-collapse", { detail: { open: isOpen.value } }));
}

function close() {
  isOpen.value = false;
  window.dispatchEvent(new CustomEvent("sz-sidebar-collapse", { detail: { open: false } }));
}

// Quando muda de conversa, envia postMessage (sem recarregar iframe)
watch(conversationId, (newId) => {
  if (iframeRef.value && iframeRef.value.contentWindow && iframeReady.value) {
    iframeRef.value.contentWindow.postMessage({
      type: 'sz-ai-conversation',
      conversation_id: newId,
    }, '*');
  }
});

// Quando muda de conta (token diferente), avisa o iframe pra recarregar dados
watch(cwToken, (newToken) => {
  if (iframeRef.value && iframeRef.value.contentWindow && iframeReady.value && newToken) {
    iframeRef.value.contentWindow.postMessage({
      type: 'sz-ai-token',
      token: newToken,
    }, '*');
  }
});

function onToggleIA() { toggle(); }

function onIframeLoad() {
  iframeReady.value = true;
  syncTheme();
}

function onMessage(e) {
  if (e.data?.type === 'sz-ai-close') close();
  if (e.data?.type === 'sz-ai-navigate' && e.data.conversation_id) {
    const cid = e.data.conversation_id;
    const user = currentUser.value;
    const accountId = user?.account_id;
    if (accountId) {
      router.push({ name: 'conversation_through_inbox', params: { conversation_id: cid } }).catch(() => {
        window.location.href = `/app/accounts/${accountId}/conversations/${cid}`;
      });
    }
  }
  if (e.data?.type === 'sz-ai-conversation-mutated') {
    // A IA modificou uma ou mais conversas. Refaz fetch sem F5.
    const ids = Array.isArray(e.data.conversation_ids) ? e.data.conversation_ids : [];
    const currentId = Number(conversationId.value);
    if (!currentId) return;
    if (ids.length && !ids.includes(currentId)) return;
    setTimeout(() => {
      try { store.dispatch('getConversation', currentId); } catch (_) {}
      try { store.dispatch('fetchPreviousMessages', { conversationId: currentId }); } catch (_) {}
    }, 400);
  }
  if (e.data?.type === 'sz-ai-open-image') {
    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.88);z-index:99999;display:flex;align-items:center;justify-content:center;cursor:zoom-out;';
    modal.onclick = (ev) => { if (ev.target.tagName !== 'IMG') modal.remove(); };
    const img = document.createElement('img');
    img.src = e.data.src;
    img.style.cssText = 'max-width:90vw;max-height:85vh;border-radius:10px;object-fit:contain;cursor:default;';
    const btn = document.createElement('button');
    btn.innerHTML = '&times;';
    btn.style.cssText = 'position:absolute;top:20px;right:20px;width:40px;height:40px;background:rgba(255,255,255,0.15);border:none;border-radius:50%;color:white;font-size:22px;cursor:pointer;display:flex;align-items:center;justify-content:center;';
    btn.onclick = () => modal.remove();
    modal.appendChild(btn);
    modal.appendChild(img);
    document.body.appendChild(modal);
    document.addEventListener('keydown', function esc(ev) { if (ev.key === 'Escape') { modal.remove(); document.removeEventListener('keydown', esc); } });
  }
}

function syncTheme() {
  if (iframeRef.value && iframeRef.value.contentDocument) {
    try {
      iframeRef.value.contentDocument.body.classList.toggle('dark', document.body.classList.contains('dark'));
    } catch(e) {}
  }
}

let darkObserver = null;

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  window.addEventListener('sz-toggle-intelligence', onToggleIA);
  window.addEventListener('message', onMessage);
  darkObserver = new MutationObserver(() => syncTheme());
  darkObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  window.removeEventListener('sz-toggle-intelligence', onToggleIA);
  window.removeEventListener('message', onMessage);
  if (darkObserver) darkObserver.disconnect();
});

defineExpose({ toggle, isOpen });
</script>

<template>
  <div
    class="sz-intelligence-panel"
    :class="{
      'sz-intelligence-open': isOpen,
      'sz-intelligence-mobile': isMobile && isOpen,
    }"
  >
    <button
      v-if="isOpen && isMobile"
      class="sz-mobile-close-btn"
      aria-label="Fechar"
      @click="close"
    >
      &times;
    </button>
    <!-- v-show mantém o iframe vivo, v-if só para primeiro render -->
    <iframe
      v-if="cwToken"
      v-show="isOpen"
      ref="iframeRef"
      :src="iframeUrl"
      class="sz-intelligence-iframe"
      allow="microphone; clipboard-read; clipboard-write"
      @load="onIframeLoad"
    />
  </div>
</template>

<style scoped>
.sz-intelligence-panel {
  width: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: none;
  height: 100%;
  position: relative;
}
.sz-intelligence-open {
  width: 380px;
}
.sz-intelligence-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
.sz-intelligence-mobile {
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100% !important;
  height: 100%;
  border-left: none;
  background: var(--white, #fff);
  transition: none;
}
.sz-mobile-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-mobile-close-btn:hover { background: rgba(0, 0, 0, 0.65); }
@media (max-width: 1200px) and (min-width: 1025px) {
  .sz-intelligence-open:not(.sz-intelligence-mobile) { width: 340px; }
}
@media (max-width: 1024px) and (min-width: 768px) {
  .sz-intelligence-open:not(.sz-intelligence-mobile) { width: 300px; }
}
@media (max-width: 767px) {
  .sz-intelligence-open {
    position: fixed; inset: 0; z-index: 9999;
    width: 100% !important; height: 100%;
    border-left: none; background: var(--white, #fff); transition: none;
  }
}
</style>
